import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import { borderRadius, transitions } from '../designTokens';
import { saveSelectedDestination, loadSelectedDestination, destinationName, manualPrefillFor } from '../lib/plannerDestination';

export const PLANNER_CHOOSER_CSS = `
.pchooser { font-family: 'Montserrat', sans-serif; color: ${B.night}; }
.pchooser .wrap { max-width: 960px; margin: 0 auto; padding: 0 clamp(16px, 4vw, 40px) 64px; }
.pchooser .serif { font-family: 'Playfair Display', serif; }
.pchooser .page-head { padding: 40px 0 6px; }
.pchooser .kicker { font-size: 12px; letter-spacing: .26em; font-weight: 700; color: ${B.forest}; text-transform: uppercase; }
.pchooser .page-head h1 { font-size: 46px; font-weight: 800; margin-top: 6px; }
.pchooser .page-head p { color: ${B.muted}; margin-top: 8px; font-size: 16px; }
.pchooser .dest-chip {
  display: inline-flex; align-items: center; gap: 8px; max-width: 100%; margin-top: 16px;
  padding: 7px 7px 7px 14px; background: #fff; border: 1.5px solid #e7e2d9; border-radius: ${borderRadius.pill}; font-size: 14px;
}
.pchooser .dest-chip .dest-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pchooser .dest-chip strong { color: ${B.forest}; }
.pchooser .chip-clear {
  flex-shrink: 0; width: 28px; height: 28px; border: none; border-radius: 50%; cursor: pointer;
  background: #f3efe8; color: ${B.muted}; font-size: 13px; line-height: 1;
}
.pchooser .cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; margin-top: 36px; }
.pchooser .card {
  background: #fff; border: 1.5px solid #e7e2d9; border-radius: ${borderRadius.xl};
  padding: 32px 28px; display: flex; flex-direction: column; gap: 14px;
  transition: transform ${transitions.fast}, box-shadow ${transitions.fast}, border-color ${transitions.fast};
}
.pchooser .card:hover { transform: translateY(-3px); box-shadow: 0 14px 34px rgba(15,25,35,0.10); border-color: #d9d2c6; }
.pchooser .card-top { display: flex; align-items: center; justify-content: space-between; }
.pchooser .icon { width: 52px; height: 52px; border-radius: 16px; display: grid; place-items: center; }
.pchooser .badge { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; padding: 5px 11px; border-radius: ${borderRadius.pill}; }
.pchooser .card h2 { font-size: 26px; font-weight: 800; margin: 6px 0 0; }
.pchooser .card p { color: ${B.muted}; font-size: 15px; line-height: 1.55; margin: 0; flex: 1; }
.pchooser .cta {
  margin-top: 8px; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif;
  font-size: 14px; font-weight: 700; padding: 13px 22px; border-radius: ${borderRadius.pill};
  display: inline-flex; align-items: center; justify-content: center; gap: 8px; align-self: flex-start;
  transition: all ${transitions.fast};
}
.pchooser .cta:hover { filter: brightness(1.08); transform: translateY(-1px); }
.pchooser .cta.manual { background: ${B.night}; color: #fff; }
.pchooser .cta.ai { background: ${B.amber}; color: ${B.night}; }
.pchooser .cta:disabled { background: #e7e2d9; color: ${B.muted}; cursor: not-allowed; filter: none; transform: none; }
@media (max-width: 720px) {
  .pchooser .page-head h1 { font-size: 32px; }
  .pchooser .cards { grid-template-columns: 1fr; gap: 16px; }
  .pchooser .card { padding: 24px 20px; }
  .pchooser .cta { align-self: stretch; }
}
`;

export const PencilIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

export const PlaneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
  </svg>
);

export default function PlannerChooserScreen({ goScreen, prefillDestination }) {
  const { t } = useTranslation();
  const [dest, setDest] = useState(() => prefillDestination || loadSelectedDestination());
  useEffect(() => {
    if (prefillDestination) { saveSelectedDestination(prefillDestination); setDest(prefillDestination); }
  }, [prefillDestination]);
  const clearDest = () => { saveSelectedDestination(null); setDest(null); };
  const destLabel = destinationName(dest).split(',')[0].trim();

  return (
    <div className="pchooser"><style>{PLANNER_CHOOSER_CSS}</style>
      <div className="wrap">
        <div className="page-head">
          <span className="kicker">{t('planner.kicker')}</span>
          <h1 className="serif">{t('planner.title')}</h1>
          <p>{t('planner.subtitle')}</p>
          {destLabel && (
            <div className="dest-chip" role="status">
              <span aria-hidden="true">📍</span>
              <span className="dest-text">{t('planner.selectedFor')} <strong>{destLabel}</strong></span>
              <button type="button" className="chip-clear focus-ring" aria-label={t('planner.clearDestination')} onClick={clearDest}>✕</button>
            </div>
          )}
        </div>

        <div className="cards">
          <div className="card">
            <div className="card-top">
              <span className="icon" style={{ background: 'rgba(78,203,141,0.16)', color: B.mint }}><PencilIcon /></span>
              <span className="badge" style={{ background: 'rgba(78,203,141,0.16)', color: B.forest }}>{t('planner.manualBadge')}</span>
            </div>
            <h2 className="serif">{t('planner.manualTitle')}</h2>
            <p>{t('planner.manualDesc')}</p>
            <button type="button" className="cta manual focus-ring"
              onClick={() => goScreen('manualPlanner', manualPrefillFor(dest))}>
              {t('planner.manualCta')} <span aria-hidden="true">→</span>
            </button>
          </div>

          <div className="card">
            <div className="card-top">
              <span className="icon" style={{ background: 'rgba(245,166,35,0.18)', color: B.amber }}><PlaneIcon /></span>
              <span className="badge" style={{ background: 'rgba(245,166,35,0.18)', color: '#9a6400' }}>{t('planner.aiBadge')}</span>
            </div>
            <h2 className="serif">{t('planner.aiTitle')}</h2>
            <p>{t('planner.aiDesc')}</p>
            <button type="button" className="cta ai focus-ring" onClick={() => goScreen('ai', dest ? { prefillDestination: dest } : null)}>
              {t('planner.aiCta')} <span aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
